import { storeLinks } from "~/utils/app-config";

const redirectPath = [
  '/openapp',
  '/challenges',
  '/tracking',
  '/explorer',
  '/profile/runs',
];

/** We should redirect to the app if the user is on a page that requires it **/
export function shouldRedirectToApp (path) {
  return redirectPath.includes(path);
}

/**
 * Contains the redirection logic extracted from the middleware for testing
 */
export function shouldRedirectToStore (path, searchParams = {}) {
  // Normalize the path (remove trailing slash and domain)
  const normalizedPath = normalizePath(path);
  const tab = searchParams.tab || '';

  // First check if the route should never redirect
  if (isExcludedRoute(normalizedPath, tab)) {
    return false;
  }

  // Then check if the route should redirect
  return isRedirectRoute(normalizedPath, tab);
}

/**
 * Normalizes a URL path for testing
 */
export function normalizePath (path) {
  // Remove domain if present
  let normalizedPath = path.replace(/^https?:\/\/[^/]+/, '');

  // Remove trailing slashes
  normalizedPath = normalizedPath.replace(/\/$/, '');

  // Split path and query parameters
  const [pathOnly] = normalizedPath.split('?');

  // Remove language prefix if present
  const locales = ['en', 'es', 'fr', 'ca', 'it'];
  const localeRegex = new RegExp(`^/(${locales.join('|')})/`);
  const pathWithoutLocale = pathOnly.replace(localeRegex, '/');

  // Return the normalized path
  return pathWithoutLocale;
}

/**
 * Extracts query parameters from a URL
 */
export function extractSearchParams (url) {
  const params = {};
  try {
    // Use URL API if available (browser)
    const urlObj = new URL(url, 'https://example.com');
    for (const [key, value] of urlObj.searchParams) {
      params[key] = value;
    }
  } catch (e) {
    // Fallback for Node.js or environments without URL API
    const queryString = url.split('?')[1] || '';
    if (queryString) {
      queryString.split('&').forEach((param) => {
        const [key, value] = param.split('=');
        if (key) params[key] = value || '';
      });
    }
  }
  return params;
}

/**
 * Checks if a route is excluded from redirection
 */
function isExcludedRoute (path, tab) {
  // Destinations tab
  if (path === '/explorer' && (!tab || tab === 'destinations')) {
    return true;
  }

  // Destinations routes
  return !!path.startsWith('/explorer/destinations/');
}

/**
 * Checks if a route should redirect to the store
 */
function isRedirectRoute (path, tab) {
  // Challenges
  if (path.startsWith('/challenges')) {
    return true;
  }

  // Openapp
  if (path.startsWith('/openapp')) {
    return true;
  }

  // Tracking
  return !!path.startsWith('/tracking');
}

// Get the mobile OS
export function getMobileOS () {
  if (process.server) return null;
  const ua = navigator.userAgent || navigator.vendor || '';
  if (/android/i.test(ua)) return 'isAndroid';
  if (/iPad|iPhone|iPod/i.test(ua)) return 'isIos';
  return null;
}

// Redirect to the store based on the mobile OS
export function redirectToStore (os) {
  const storeLink = os === "isIos" ? storeLinks.apple : os === "isAndroid" ? storeLinks.google : null;
  if (storeLink) {
    window.location.href = storeLink;
  }
}

export function redirectToAppIfInstalled (os, path, searchParams = {}) {
  let deeplinkUrl;
  const query = new URLSearchParams(searchParams).toString();
  const deeplinkPath = `${path}${query ? '?' + query : ''}`;
  const iosDeeplinkUrl = `onpisteapp:/${deeplinkPath}`;
  const androidDeeplinkUrl = `intent://onpiste.com${deeplinkPath}#Intent;scheme=https;action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;package=com.rossignol.onpiste;S.browser_fallback_url=${storeLinks.google};end;`;
  if (os === "isIos") {
    deeplinkUrl = iosDeeplinkUrl;
  } else if (os === "isAndroid") {
    deeplinkUrl = androidDeeplinkUrl;
  }
  // Try open deeplink
  window.location.href = deeplinkUrl;
}
